/**
 * CurriculumTree — the LEFT browse tree for a non-US jurisdiction.
 *
 * Sibling of CategoryTree, never a replacement: Printables renders exactly one
 * of the two. Every US state, every territory that is not curriculum-capable,
 * and the no-jurisdiction case keep CategoryTree byte-for-byte. See the
 * `useCurriculumTree` switch in Printables.jsx.
 *
 * Shape (contract 2026-08-24):
 *   Subject → Document → Group → Standard
 * with a SERVER-SIDE collapse: a subject that owns exactly one document returns
 * that document's children directly and reports it as `collapsedDocument`, so
 * half the tree is 3 levels deep. The client never collapses anything itself.
 *
 * Node ids are opaque strings (`cur.v1.<type>.<JUR>[.<key>]`) minted by the
 * worker. This component only ever echoes one back — it never parses, builds or
 * hashes an id. The jurisdiction is baked into every id, which is why the
 * children/detail fetches deliberately do NOT send ?jurisdiction.
 *
 * TWO RULES THAT LOOK LIKE BUGS AND ARE NOT:
 *
 * 1. Names follow the UI language WHERE A TRANSLATION EXISTS, and are verbatim
 *    otherwise (owner spec 2026-09-08: "if I change language it remains on
 *    that curriculum but is now in that language"). Every row goes through the
 *    same categoryLabel() lookup CategoryTree uses. In practice that means the
 *    15 English-language curricula (CXC / OECS …) translate at the subject
 *    level — "Physics" → "Física", "Social Studies" → "Estudos sociais" — and
 *    ministry wording in es / pt / fr ("Matemática", "Unidade temática", "Eje")
 *    stays as published, because no catalog key matches it. Translating those
 *    is a data project (37 ministries × 4 languages), not a client lookup.
 *
 * 2. A node reading 0 posters is the NORMAL case, not a failure. Live stock is
 *    1–10 posters per jurisdiction against 1,000+ standards, so >99% of nodes
 *    are empty. Empty nodes are never hidden and never greyed out — the tree is
 *    the country's official curriculum, and a missing "Matemática" would read as
 *    broken data. THE ONLY THING DRAWN TO THE RIGHT OF A NAME IS THE PRINTABLES
 *    PILL, and only when posterCount > 0 (owner spec 2026-09-08: "there must
 *    only be that little banner … with number of printables"). Structural size
 *    and the document's grade live in the row's tooltip, nowhere else — the
 *    earlier bare "2" that was really "2 documents" is exactly what a second
 *    number in that slot does to a reader.
 *
 * `hasChildren` comes from the STRUCTURE the worker reports, never from
 * posterCount. CategoryTree's `posterCount > 0 || children.length || depth < 3`
 * heuristic must not be copied here — with this stock it would make almost every
 * node look childless.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatNumber } from '../../i18n/formatters';
import { categoryLabel } from './categoryLabel';

// Long child lists are the document's genuine structure (worst case measured:
// 150 standards under DOM "Matemática — 6º grado / Medición", 122 groups under
// FRA "Français cycle 4"), so they are paginated in place, never restructured.
const CHILD_PAGE = 25;

const TYPE_COLORS = {
  subject: '#1AB0F2',
  document: '#7F4BF6',
  group: '#06b6d4',
  standard: '#8A8FA8',
};

const EXPANDED_KEY = 'printables_curriculum_expanded';

// Same three keys PrintableCard/PrintableDetail use — the grade chip on a
// document node must read identically to the grade chip on a poster card.
function gradeLabel(min, max) {
  if (min == null && max == null) return null;
  const fmt = (g) => (g === 0 ? i18n.t('printables:grade.kindergarten') : String(g));
  if (min === max) return i18n.t('printables:grade.single', { grade: fmt(min) });
  return i18n.t('printables:grade.range', { min: fmt(min), max: fmt(max) });
}

function readExpanded(jurisdiction) {
  try {
    const raw = sessionStorage.getItem(EXPANDED_KEY);
    if (!raw) return new Set();
    const saved = JSON.parse(raw);
    // Node ids are jurisdiction-scoped: a set saved for BRA resolves to nothing
    // under FRA, so it is dropped rather than replayed as 404s.
    if (!saved || saved.jurisdiction !== jurisdiction) return new Set();
    return new Set(saved.ids || []);
  } catch { return new Set(); }
}

function getIndent(depth) {
  return 8 + depth * 3;
}

export default function CurriculumTree({
  jurisdiction,
  filters,
  selectedNodeId,
  onNodeSelect,
  onViewAll,
  totalPosters,
  fetchCurriculumTree,
  fetchCurriculumChildren,
}) {
  const { t } = useTranslation('printables');

  // Scalar key so the effects below can depend on the filter without churning
  // on object identity; the ref keeps async callbacks reading the fresh value.
  // Jurisdiction is part of the key: it changes which tree this even is.
  // `ui` is part of the key: a language switch must refetch so the worker can
  // answer with displayNames in the new language (migration 0038).
  const filterKey = `${jurisdiction || ''}|${filters?.language ?? ''}|${filters?.curriculum ?? ''}|${filters?.ui ?? ''}`;
  const filterRef = useRef(filters);
  filterRef.current = filters;

  const [roots, setRoots] = useState([]);
  const [rootTotal, setRootTotal] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => readExpanded(jurisdiction));
  // nodeId → { children, groupLevelLabel, collapsedDocument }
  const [childrenMap, setChildrenMap] = useState({});
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [showAllIds, setShowAllIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const restoredRef = useRef(false);

  // Region / language / curriculum changed → every node id and every count in
  // this tree is now wrong (unlike CategoryTree, where only the counts move).
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setLoaded(false);
    setRoots([]);
    setRootTotal(null);
    setChildrenMap({});
    setLoadingIds(new Set());
    setShowAllIds(new Set());
    setExpandedIds(readExpanded(jurisdiction));
    restoredRef.current = false;
  }, [filterKey, jurisdiction]);

  useEffect(() => {
    if (loaded || !jurisdiction) return;
    let cancelled = false;
    fetchCurriculumTree(jurisdiction, filterRef.current || {})
      .then((data) => {
        if (cancelled) return;
        setRoots(data?.nodes || []);
        setRootTotal(typeof data?.totalPosters === 'number' ? data.totalPosters : null);
        setLoaded(true);
      })
      // An unreachable worker degrades to the "no curriculum" line, never an
      // error banner — same graceful-failure rule as the alignment chips.
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [fetchCurriculumTree, jurisdiction, loaded, filterKey]);

  useEffect(() => {
    try {
      sessionStorage.setItem(EXPANDED_KEY, JSON.stringify({ jurisdiction, ids: [...expandedIds] }));
    } catch { /* private mode — the tree just reopens collapsed */ }
  }, [expandedIds, jurisdiction]);

  // A node id that no longer resolves (a re-ingest changed a subject string or
  // dropped a set) is a 404. Treat it as "that branch is gone": forget it and,
  // if it was the selection, fall back to the subject roots. Never a banner.
  const forgetNode = useCallback((nodeId) => {
    setExpandedIds((prev) => { const s = new Set(prev); s.delete(nodeId); return s; });
    setChildrenMap((prev) => { const m = { ...prev }; delete m[nodeId]; return m; });
    if (selectedNodeId === nodeId) onNodeSelect(null);
  }, [selectedNodeId, onNodeSelect]);

  const loadChildren = useCallback(async (nodeId) => {
    setLoadingIds((prev) => new Set(prev).add(nodeId));
    try {
      const data = await fetchCurriculumChildren(nodeId, filterRef.current || {});
      setChildrenMap((prev) => ({
        ...prev,
        [nodeId]: {
          children: data?.children || [],
          groupLevelLabel: data?.groupLevelLabelDisplay ?? data?.groupLevelLabel ?? null,
          collapsedDocument: data?.collapsedDocument ?? null,
        },
      }));
    } catch (err) {
      if (err?.status === 404) forgetNode(nodeId);
    }
    setLoadingIds((prev) => { const s = new Set(prev); s.delete(nodeId); return s; });
  }, [fetchCurriculumChildren, forgetNode]);

  // Restore children for previously expanded nodes after roots load.
  useEffect(() => {
    if (!loaded || restoredRef.current || expandedIds.size === 0) return;
    restoredRef.current = true;
    expandedIds.forEach((id) => { loadChildren(id); });
  }, [loaded, expandedIds, loadChildren]);

  // Reads `expandedIds` directly rather than deciding inside a functional
  // updater: React 18 defers the updater to the render pass, so a flag set
  // inside it is still false on the very next line.
  const toggleExpand = useCallback((nodeId) => {
    const next = new Set(expandedIds);
    if (next.has(nodeId)) {
      next.delete(nodeId);
      setExpandedIds(next);
      return;
    }
    next.add(nodeId);
    setExpandedIds(next);
    if (!childrenMap[nodeId]) loadChildren(nodeId);
  }, [expandedIds, childrenMap, loadChildren]);

  const renderNode = (node, depth = 0) => {
    const isExpanded = expandedIds.has(node.id);
    const isLoading = loadingIds.has(node.id);
    const isSelected = selectedNodeId === node.id;
    const bundle = childrenMap[node.id];
    const kids = bundle?.children || [];
    // Structure only — NEVER posterCount (see the header note).
    const hasChildren = node.hasChildren === true;
    const color = TYPE_COLORS[node.type] || '#8A8FA8';
    const indent = getIndent(depth);
    const isRoot = depth === 0;

    const grade = node.type === 'document' ? gradeLabel(node.gradeMin, node.gradeMax) : null;

    // The printables pill is the ONLY thing drawn beside a name (header rule 2).
    // Structural size and grade go to the tooltip. This slot once carried a
    // bare "2" that was really "2 documents" — BHS Physics, 0 printables — and
    // was read as two printables (owner, 2026-09-04); then a spelled-out
    // "2 sections", which the owner also did not want beside the name.
    const posterCount = node.posterCount > 0 ? node.posterCount : 0;
    // Worker-side translation first (every ministry, every UI language —
    // migration 0038), then the client catalog (English curricula), then the
    // ministry's own wording.
    const label = node.displayName || categoryLabel(t, node.name);
    let structural = null;
    if (node.type === 'subject' && node.childCount > 0) {
      structural = t('tree.sectionsCount', { count: node.childCount });
    } else if ((node.type === 'document' || node.type === 'group') && node.standardCount > 0) {
      structural = t('tree.standardsCount', { count: node.standardCount });
    }
    const tooltip = [
      node.identifier ? `${node.identifier} — ${label}` : label,
      grade, structural,
    ].filter(Boolean).join(' · ');

    const shown = showAllIds.has(node.id) ? kids : kids.slice(0, CHILD_PAGE);
    const hiddenCount = kids.length - shown.length;

    return (
      <div key={node.id}>
        <div
          onClick={() => { onNodeSelect(node.id); if (hasChildren) toggleExpand(node.id); }}
          /* Ellipsized + tooltip: PR's statement_label values are full
             parent-standard sentences (100+ chars), not short node names. The
             tooltip is also where grade and structural size live now. */
          title={tooltip}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 8px',
            cursor: 'pointer', borderRadius: 6,
            background: isSelected ? 'rgba(26, 176, 242, 0.15)' : 'transparent',
            borderLeft: depth > 0 ? `2px solid ${isSelected ? color : 'rgba(27,37,80,0.057)'}` : 'none',
            marginLeft: depth > 0 ? indent - 6 : 0,
            paddingLeft: depth > 0 ? 8 : indent,
            transition: 'all 0.15s ease',
            minHeight: 40, borderRadius: 12,
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(27,37,80,0.038)'; }}
          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
        >
          {/* Chevron */}
          {hasChildren ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
              <path d="M9 6l6 6-6 6" stroke="rgba(27,37,80,0.332)" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          ) : <div style={{ width: 12, flexShrink: 0 }} />}

          {/* Colour dot — only on the first two levels, to save width */}
          {depth <= 1 && (
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
          )}

          {/* Standard code — the official identifier, rendered as a prefix */}
          {node.identifier && (
            <span style={{
              fontSize: '0.65rem', color: 'rgba(27,37,80,0.332)', flexShrink: 0,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}>
              {node.identifier}
            </span>
          )}

          {/* Name — follows the UI language where the catalog has it, verbatim
              ministry wording otherwise (header rule 1). */}
          <span style={{
            flex: 1,
            fontSize: isRoot ? '1rem' : '0.94rem',
            fontWeight: isSelected ? 600 : isRoot ? 500 : 400,
            color: isSelected ? '#0A6A99' : isRoot ? 'rgba(27,37,80,0.76)' : 'rgba(27,37,80,0.57)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}>
            {label}
          </span>

          {/* Nothing else sits between the name and the printables pill —
              grade and structural size are in the tooltip (header rule 2). */}

          {/* Printables count — a pill, and the ONLY thing in this row that is
              ever a pill. The glyph is what makes it self-describing at a
              glance next to the spelled-out structural size above. */}
          {posterCount > 0 && (
            <span
              title={t('tree.postersCount', { count: posterCount })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontSize: '0.8rem', fontWeight: 800, color: 'rgba(27,37,80,0.6)',
                background: '#FFFFFF', padding: '1px 5px', borderRadius: 8,
                fontWeight: 500, flexShrink: 0, lineHeight: 1.4,
              }}
            >
              <svg width="8" height="9" viewBox="0 0 8 9" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                <rect x="0.6" y="0.6" width="6.8" height="7.8" rx="1" stroke="currentColor" strokeWidth="1.1" />
                <path d="M2.4 3.2h3.2M2.4 5.2h2.2" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
              </svg>
              {formatNumber(posterCount)}
            </span>
          )}
        </div>

        {/* Children */}
        {isExpanded && (
          <div>
            {isLoading && (
              <div style={{ paddingLeft: getIndent(depth + 1) + 12, padding: '4px 8px', fontSize: '0.7rem', color: 'rgba(27,37,80,0.237)' }}>
                {t('tree.loading')}
              </div>
            )}

            {/* The single document a subject collapsed into — a caption, not a
                node, so the path stays 3 levels deep but the teacher still sees
                which ministry document they are looking at. */}
            {bundle?.collapsedDocument && (
              <div style={{
                paddingLeft: getIndent(depth + 1) + 12, paddingRight: 8, paddingTop: 2, paddingBottom: 2,
                fontSize: '0.65rem', color: 'rgba(27,37,80,0.266)', lineHeight: 1.35,
              }}>
                {bundle.collapsedDocument.displayName || bundle.collapsedDocument.name}
                {gradeLabel(bundle.collapsedDocument.gradeMin, bundle.collapsedDocument.gradeMax)
                  ? ` · ${gradeLabel(bundle.collapsedDocument.gradeMin, bundle.collapsedDocument.gradeMax)}`
                  : ''}
              </div>
            )}

            {/* The ministry's OWN name for this level ("Unidade temática",
                "Eje", "Bloque", "Componente del área"). Null for most
                jurisdictions — and then nothing is drawn, because inventing a
                generic word like "Group" would claim a level the curriculum
                document does not have. */}
            {bundle?.groupLevelLabel && (
              <div style={{
                paddingLeft: getIndent(depth + 1) + 12, paddingRight: 8, paddingTop: 2, paddingBottom: 2,
                fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
                color: 'rgba(27,37,80,0.19)',
              }}>
                {bundle.groupLevelLabel}
              </div>
            )}

            {/* Group rows first, then any ungrouped standards inline at the same
                indent — a document whose ministry states no level exists simply
                has standards hanging here. No "Unknown" / "Other" bucket is ever
                drawn: that would imply the data is missing when it is not. */}
            {shown.map(child => renderNode(child, depth + 1))}

            {hiddenCount > 0 && (
              <div
                onClick={(e) => { e.stopPropagation(); setShowAllIds(prev => new Set(prev).add(node.id)); }}
                style={{
                  paddingLeft: getIndent(depth + 1) + 12, paddingRight: 8, paddingTop: 4, paddingBottom: 4,
                  fontSize: '0.68rem', color: 'rgba(26,176,242,0.85)', cursor: 'pointer',
                }}
              >
                {t('tree.showAll', { count: kids.length, formatted: formatNumber(kids.length) })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const badgeTotal = rootTotal != null ? rootTotal : totalPosters;

  return (
    <div>
      <div className="bv-pr-label" style={{ padding: '0 4px' }}>
        {t('tree.curriculum')}
      </div>

      {/* All Printables — identical affordance to CategoryTree's */}
      <div
        onClick={() => onViewAll ? onViewAll() : onNodeSelect(null)}
        title={t('tree.allPrintables')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 8px', cursor: 'pointer', borderRadius: 6,
          background: selectedNodeId === null ? 'rgba(26, 176, 242, 0.15)' : 'transparent',
          transition: 'background 0.15s ease',
          minHeight: 40, borderRadius: 12,
        }}
        onMouseEnter={e => { if (selectedNodeId !== null) e.currentTarget.style.background = 'rgba(27,37,80,0.038)'; }}
        onMouseLeave={e => { if (selectedNodeId !== null) e.currentTarget.style.background = 'transparent'; }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" stroke={selectedNodeId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" stroke={selectedNodeId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" stroke={selectedNodeId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" stroke={selectedNodeId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
        </svg>
        <span style={{ fontSize: '1rem', fontWeight: selectedNodeId === null ? 900 : 700, color: selectedNodeId === null ? '#0A6A99' : 'rgba(27,37,80,0.76)' }}>
          {t('tree.allPrintables')}
        </span>
        {badgeTotal > 0 && (
          <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'rgba(27,37,80,0.6)', background: '#EEF0F6', padding: '2px 8px', borderRadius: 8, fontWeight: 500, marginLeft: 'auto', lineHeight: 1.4 }}>
            {formatNumber(badgeTotal)}
          </span>
        )}
      </div>

      <div style={{ marginTop: 2 }}>
        {!loaded && (
          <div style={{ padding: '6px 8px', fontSize: '0.7rem', color: 'rgba(27,37,80,0.237)' }}>
            {t('tree.loading')}
          </div>
        )}
        {loaded && roots.length === 0 && (
          <div style={{ padding: '6px 8px', fontSize: '0.7rem', color: 'rgba(27,37,80,0.237)', lineHeight: 1.4 }}>
            {t('tree.noCurriculum')}
          </div>
        )}
        {roots.map(root => renderNode(root, 0))}
      </div>
    </div>
  );
}
